import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Loader2, ShoppingBag, Tag, PackageX } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { fetchProducts, fetchProductCategories } from '../lib/api';

const formatINR = (rupees) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(rupees);

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [cat, setCat] = useState('All');
  const [categories, setCategories] = useState(['All']);

  useEffect(() => {
    fetchProductCategories()
      .then((res) => setCategories(['All', ...(res.categories || [])]))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (cat && cat !== 'All') params.category = cat;
    fetchProducts(params)
      .then(setProducts)
      .catch(() => toast.error('Could not load products.'))
      .finally(() => setLoading(false));
  }, [cat]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) =>
      [p.name, p.description, p.category].filter(Boolean).some((v) => v.toLowerCase().includes(q))
    );
  }, [products, query]);

  return (
    <div>
      <section className="bg-[#0F1B2D] text-[#F8F2E4] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-xs tracking-[0.3em] text-[#E89530] mb-5">SHOP</p>
          <h1 className="font-serif-besley text-5xl md:text-6xl mb-5">Legal templates &amp; materials</h1>
          <p className="text-[#C9BFA8] text-lg max-w-2xl">
            Ready-to-use legal templates, curated books, and merchandise — all payable in INR via UPI, cards, or net banking.
          </p>
        </div>
      </section>

      <section className="bg-[#F8F2E4] py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="relative max-w-xl mb-8">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a5a5a]" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search products…"
              className="pl-11 h-12 bg-white border-[#E8DCC0] focus-visible:ring-[#2D4373]"
            />
          </div>

          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className={`px-4 py-2 rounded-full text-sm border transition-all ${
                  cat === c
                    ? 'bg-[#0F1B2D] text-[#F8F2E4] border-[#0F1B2D]'
                    : 'bg-white text-[#0F1B2D] border-[#E8DCC0] hover:border-[#0F1B2D]'
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24 text-[#5a5a5a]">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-[#5a5a5a] bg-white border border-dashed border-[#E8DCC0] rounded-2xl">
              <PackageX className="w-10 h-10 mx-auto mb-3 text-[#A36B1F]" strokeWidth={1.5} />
              <p className="font-medium text-[#0F1B2D]">No products yet.</p>
              <p className="text-sm mt-1">Please check back soon.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p) => (
                <article key={p.id} className="pillar-card bg-[#EDE3CC] rounded-2xl overflow-hidden flex flex-col">
                  {p.image_url ? (
                    <div className="aspect-video bg-[#0F1B2D] overflow-hidden">
                      <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    </div>
                  ) : (
                    <div className="aspect-video bg-gradient-to-br from-[#0F1B2D] to-[#2D4373] flex items-center justify-center">
                      <ShoppingBag className="w-14 h-14 text-[#E89530] opacity-70" strokeWidth={1.2} />
                    </div>
                  )}
                  <div className="p-6 flex flex-col flex-1">
                    <Badge variant="secondary" className="self-start bg-white text-[#0F1B2D] border border-[#D6CAB0] mb-3">
                      <Tag className="w-3 h-3 mr-1" /> {p.category}
                    </Badge>
                    <h3 className="font-serif-besley text-xl text-[#0F1B2D] leading-tight mb-2">{p.name}</h3>
                    {p.description ? <p className="text-sm text-[#3a3a3a] leading-relaxed mb-5 line-clamp-3">{p.description}</p> : null}
                    <div className="mt-auto flex items-center justify-between">
                      <div className="font-serif-besley text-2xl text-[#0F1B2D]">{formatINR(p.price_inr)}</div>
                      <Link
                        to={`/shop/checkout?type=product&id=${p.id}`}
                        className="btn-orange inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium"
                      >
                        Buy now
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Shop;
